import { z } from 'zod'
import { expediteurCorrespond } from './consignes'
import { CLASSEMENTS_MAIL } from './domaine'
import {
  type Perimetre,
  peutAdministrer,
  peutDesignerEntite,
  peutEcrireEntite,
  peutLireEntite,
} from './droits'
import { CLOISON_COMMUNE, cloisonDe } from './espaces'
import type { Rattachement } from './pieces'
import { rendezVousSante } from './sante'

// Boîtes Gmail personnelles : chaque personne connecte ses boîtes, l'appli y repère les
// demandes administratives. Minimiser ce qui est lu et gardé : un mail sans rapport ne
// laisse que son identifiant et une empreinte. Fonctions pures ; les services appliquent.

/** Premier passage (ou point d'historique expiré) : les trente derniers jours. */
export const JOURS_PREMIER_PASSAGE = 30

/**
 * Exclusions Gmail de toute requête de l'appli : ni promotions, ni réseaux sociaux, ni forums,
 * ni envoyés, brouillons, discussions, spam ou corbeille.
 */
export const EXCLUSIONS_GMAIL: readonly string[] = [
  '-category:promotions',
  '-category:social',
  '-category:forums',
  '-in:sent',
  '-in:drafts',
  '-in:chats',
  '-in:spam',
  '-in:trash',
]

/** Filtre Gmail du premier passage : les trente derniers jours, avec les exclusions. */
export const REQUETE_PREMIER_PASSAGE = [
  `newer_than:${JOURS_PREMIER_PASSAGE}d`,
  ...EXCLUSIONS_GMAIL,
].join(' ')

/** Les mêmes exclusions, sur les libellés Gmail des messages venus de l'historique. */
const LIBELLES_EXCLUS = new Set([
  'CATEGORY_PROMOTIONS',
  'CATEGORY_SOCIAL',
  'CATEGORY_FORUMS',
  'SENT',
  'DRAFT',
  'CHAT',
  'SPAM',
  'TRASH',
])

export function exclueParLibelles(libelles: readonly string[]): boolean {
  return libelles.some((l) => LIBELLES_EXCLUS.has(l))
}

// Codes temporaires (authentification à deux facteurs, confirmation de connexion) : la
// personne les attend devant son écran, ils expirent en quelques minutes. Jamais une
// démarche : traités comme sans rapport, avant même le classement.

const normaliser = (t: string) =>
  t.normalize('NFD').replace(/\p{M}/gu, '').toLowerCase().replace(/\s+/g, ' ').trim()

const MOTIFS_CODE_TEMPORAIRE = [
  /\bcodes? (?:d'|de |a )?(?:authentification|verification|confirmation|securite|connexion|validation|activation|usage unique)\b/,
  /\bcodes? (?:temporaire|unique|otp)\b/,
  /\bmot de passe (?:temporaire|unique|a usage unique)\b/,
  /\b(?:otp|2fa)\b/,
  /\bone[- ]time (?:password|passcode|code)\b/,
  /\b(?:verification|security|authentication|login|confirmation) code\b/,
]

/** Un de ces textes (objet, extrait, résumé) annonce-t-il un code temporaire ? */
export function codeTemporaire(...textes: (string | null | undefined)[]): boolean {
  return textes.some((t) => {
    if (!t) return false
    const n = normaliser(t).replace(/[’`]/g, "'")
    return MOTIFS_CODE_TEMPORAIRE.some((m) => m.test(n))
  })
}

/**
 * Connexion d'une boîte : l'adresse choisie (proposée à Google, exigée au retour) et, si
 * elle est donnée, l'entité de la boîte.
 */
export const boiteConnexion = z.object({
  adresse: z.email().max(254),
  entiteId: z.uuid().optional(),
})

/** Entité d'une boîte (null : aucune). */
export const boiteEntite = z.object({ entiteId: z.uuid().nullable() })

/**
 * Boîte rattachée à un espace privé (D6) : faux, elle ne range que dans cet espace ; vrai,
 * dans tout le périmètre de sa propriétaire. Réglé par elle seule.
 */
export const boiteRangement = z.object({ rangerHorsEspace: z.boolean() })

// Entité d'une boîte : une entité commune dit qui voit la boîte (pierremboutet@… pour le
// foyer), pas où vont ses mails, classés un par un (une facture de Flow Design peut arriver
// dans une boîte du foyer). Un espace privé borne aussi son tri : ses mails ne vont que dans
// cet espace, sauf `rangerHorsEspace` (D6, `perimetreDeBoite`).

type BoiteVue = { personneId: string; entiteId: string | null }

/** Boîte vue pour un rattachement : son entité est-elle un espace privé ? */
type BoiteRattachee = BoiteVue & { entitePrivee?: boolean }

/** Qui lit tout voit toutes les boîtes ; les autres, les leurs et celles de leurs entités. */
export function peutVoirBoite(p: Perimetre, acteurId: string, b: BoiteVue): boolean {
  return b.personneId === acteurId || peutLireEntite(p, b.entiteId)
}

/**
 * Écrire dans une boîte (un brouillon Gmail ; décision de Pierre-Marie du 02/11/2026 : toute
 * boîte qu'on voit) : la sienne, ou celle d'une entité où l'on écrit ; sans entité, un
 * administrateur. Jamais par un accès temporaire (lecture seule) ni dans une entité archivée.
 */
export function peutEcrireDansBoite(
  p: Perimetre,
  acteurId: string,
  b: { personneId: string; entite: { id: string; archivee: boolean } | null },
): boolean {
  if (b.personneId === acteurId) return true
  return b.entite ? peutEcrireEntite(p, b.entite) : peutAdministrer(p)
}

/**
 * Choisir l'entité d'une boîte : sa propriétaire ou un administrateur, parmi ses entités. La
 * boîte d'un espace privé, sa propriétaire seule ; et un administrateur ne range jamais la
 * boîte d'une autre personne dans un espace privé (elle sortirait de la vue des autres).
 */
export function peutRattacherBoite(
  p: Perimetre,
  acteurId: string,
  b: BoiteRattachee,
  cible: { id: string; archivee: boolean; privee?: boolean } | null,
): boolean {
  if (b.personneId !== acteurId && (!p.administrateur || b.entitePrivee || cible?.privee))
    return false
  return cible === null || peutEcrireEntite(p, cible)
}

/** En dessous, le classement est un doute : un humain vérifie. */
export const SEUIL_CONFIANCE_MAIL = 0.7

/** Classement d'un mail sur ses métadonnées (sortie de Claude, validée). */
export const classementMail = z.object({
  classement: z.enum(CLASSEMENTS_MAIL),
  /** Nom d'une entité du référentiel, tel quel ; null si aucune ne correspond. */
  entite: z.string().max(200).nullable(),
  /** Ce qui est demandé, en quelques mots. */
  resume: z.string().max(200).nullable(),
  /** Date limite lue dans l'objet ou l'extrait. */
  dateLimite: z.iso.date().nullable(),
  confiance: z.number().min(0).max(1),
  /** Consigne de tri que Claude juge applicable (identifiant), d'après son « quand ». */
  consigne: z.string().max(64).nullable().optional(),
  /**
   * Rendez-vous médical que le mail confirme ou rappelle (Doctolib, cabinet) : noté comme soin
   * du foyer, que le mail soit gardé ou non (rien du mail n'est gardé pour autant).
   */
  rendezVousSante: rendezVousSante.nullable().optional(),
})
export type ClassementMail = z.infer<typeof classementMail>

/**
 * Mails gardés d'une boîte : ceux rattachés à une entité où sa propriétaire écrit, et sans
 * entité pour un administrateur. Le reste est jeté comme sans rapport : sinon les
 * administrateurs verraient le courrier personnel d'une associée, et la boîte d'un
 * administrateur garderait un mail classé sur un espace privé dont il n'est pas titulaire.
 */
export function mailVisibleDuProprietaire(perimetre: Perimetre, entiteId: string | null): boolean {
  // Garder le mail, c'est écrire sur son entité : jamais celle d'un accès temporaire, ni
  // l'espace privé d'une autre personne (absent de l'écriture, même d'un administrateur). Le
  // compte assistant n'a pas de boîte et n'écrit rien en direct.
  if (perimetre.lecture === 'tout') return false
  return peutDesignerEntite(perimetre, entiteId)
}

export type NatureMail = 'demande' | 'piece'

export type DecisionMail =
  | { garder: false }
  | { garder: true; entiteId: string | null; nature: NatureMail; aVerifier: string[] }

/**
 * Ce qu'on garde d'un mail classé. Sans rapport ou code temporaire : rien. Sortie illisible (null) : à vérifier
 * dans une boîte d'administrateur, jetée ailleurs. Entité inconnue, archivée ou absente,
 * confiance basse : gardé, à vérifier. Puis la règle de visibilité du propriétaire.
 */
export function deciderMail(e: {
  classement: ClassementMail | null
  motifErreur?: string
  entites: readonly { id: string; nom: string; archivee: boolean }[]
  perimetre: Perimetre
  seuil?: number
  /**
   * Consigne de tri retenue : son entité l'emporte sur celle que Claude a lue ; déclenchée
   * par l'expéditeur (déterministe), elle est sûre et la confiance ne compte plus. Déclenchée
   * et disant que le mail vaut pièce (`vautPiece`), elle le garde comme pièce même si Claude le
   * voyait sans rapport : un humain a dit que ce mail est la facture (décision du 20/10/2026).
   */
  consigne?: { entiteId: string | null; deterministe: boolean; vautPiece?: boolean } | null
}): DecisionMail {
  const repeche =
    e.classement?.classement === 'sans_rapport' &&
    !codeTemporaire(e.classement.resume) &&
    e.consigne?.deterministe === true &&
    e.consigne.vautPiece === true
  const c = repeche ? { ...e.classement!, classement: 'facture_ou_piece' as const } : e.classement
  if (!c) {
    if (!mailVisibleDuProprietaire(e.perimetre, null)) return { garder: false }
    const motif = e.motifErreur
      ? `Classement impossible (${e.motifErreur})`
      : 'Classement impossible'
    return {
      garder: true,
      entiteId: null,
      nature: 'demande',
      aVerifier: [`${motif} : à vérifier.`],
    }
  }
  if (c.classement === 'sans_rapport' || codeTemporaire(c.resume)) return { garder: false }

  const aVerifier: string[] = []
  let entiteId: string | null = null
  const parConsigne = e.consigne?.entiteId
    ? e.entites.find((x) => x.id === e.consigne!.entiteId)
    : undefined
  if (parConsigne) {
    if (parConsigne.archivee) aVerifier.push(`Entité « ${parConsigne.nom} » archivée.`)
    else entiteId = parConsigne.id
  } else if (c.entite === null) aVerifier.push('Entité non reconnue.')
  else {
    const trouvee = e.entites.find((x) => normaliser(x.nom) === normaliser(c.entite!))
    if (!trouvee) aVerifier.push(`Entité « ${c.entite} » inconnue du référentiel.`)
    else if (trouvee.archivee) aVerifier.push(`Entité « ${trouvee.nom} » archivée.`)
    else entiteId = trouvee.id
  }
  const seuil = e.seuil ?? SEUIL_CONFIANCE_MAIL
  // Repêché par la consigne : la confiance de Claude portait sur « sans rapport ».
  if (c.confiance < seuil && !(parConsigne && e.consigne!.deterministe) && !repeche)
    aVerifier.push(`Classement peu sûr (confiance ${Math.round(c.confiance * 100)} %).`)

  if (!mailVisibleDuProprietaire(e.perimetre, entiteId)) return { garder: false }
  return {
    garder: true,
    entiteId,
    nature: c.classement === 'demande_admin' ? 'demande' : 'piece',
    aVerifier,
  }
}

/**
 * Le rendez-vous médical repéré dans ce mail est-il du contenu d'un espace privé ? Oui si le
 * mail y est gardé, ou si, écarté, Claude l'a lu comme celui d'un espace : il n'est alors pas
 * noté au foyer commun (rien ne traverse la cloison, et la santé est coupée dans l'espace).
 */
export function rendezVousDeLEspace(
  decision: DecisionMail,
  entiteLue: string | null,
  entites: readonly { id: string; nom: string; privee: boolean }[],
): boolean {
  if (decision.garder) return entites.some((e) => e.id === decision.entiteId && e.privee)
  return (
    entiteLue !== null &&
    entites.some((e) => e.privee && normaliser(e.nom) === normaliser(entiteLue))
  )
}

export type ActionTacheMail = 'traiter_demande' | 'recuperer_piece' | 'verifier_mail'

/** Le mail lu par Claude avec les précisions de sa consigne : reste-t-il un geste à faire ? */
export type ConsigneLue = { aFaire: boolean }

/**
 * Effets d'un mail gardé, une fois ses pièces jointes connues : ses PDF et XML entrent
 * dans le circuit des pièces ; une demande donne une tâche ; une pièce annoncée sans
 * pièce jointe, une tâche pour aller la chercher ; un doute, une décision. Une pièce jointe,
 * même douteuse, n'ouvre rien ici : son dossier (« Trancher le mail ») s'ouvre une fois
 * les pièces lues, si une décision reste à prendre. Une consigne à précisions lue avec le
 * mail (`lue`) dit le geste qui reste, une demande à traiter même pour une pièce annoncée,
 * ou qu'il n'y a rien à faire : la tâche naît faite, à constater (décision de Pierre-Marie
 * du 14/10/2026).
 */
export function effetsMail(
  d: Extract<DecisionMail, { garder: true }>,
  piecesJointes: number,
  lue: ConsigneLue | null = null,
): {
  tache: { type: 'action' | 'decision'; action: ActionTacheMail; faite?: true } | null
  recevoirPieces: boolean
} {
  const recevoirPieces = piecesJointes > 0
  if (d.nature === 'piece' && recevoirPieces) return { tache: null, recevoirPieces }
  if (d.aVerifier.length > 0)
    return { tache: { type: 'decision', action: 'verifier_mail' }, recevoirPieces }
  if (lue)
    return {
      tache: { type: 'action', action: 'traiter_demande', ...(!lue.aFaire && { faite: true }) },
      recevoirPieces,
    }
  if (d.nature === 'demande')
    return { tache: { type: 'action', action: 'traiter_demande' }, recevoirPieces }
  return { tache: { type: 'action', action: 'recuperer_piece' }, recevoirPieces }
}

export type IssueConsigneAppliquee =
  | { issue: 'fermer'; motif: string }
  | { issue: 'devenir'; tache: { type: 'action' | 'decision'; action: ActionTacheMail } }
  | { issue: 'garder' }
  | { issue: 'laisser'; raison: string }

/**
 * Ce que devient une demande ouverte née d'un mail quand une consigne lui est appliquée
 * après coup : ce que le tri lui aurait donné (décision de Pierre-Marie du 14/10/2026, une
 * « Vérifier un mail » restée ouverte une fois la consigne appliquée). La consigne qui ignore,
 * ou ses précisions lues avec le mail qui ne laissent rien à faire : fermée, si tous les
 * mails de la tâche sont les siens. Une « Vérifier un mail » dont l'entité est connue : la
 * vérification est faite, elle devient la tâche du tri (le geste lu, sinon la demande ou la
 * pièce à récupérer) ; les pièces jointes d'une pièce restent à leur dossier. Claude lit
 * que la consigne ne vaut pas pour ce mail : rien ne change, comme au tri.
 */
export function issueConsigneAppliquee(e: {
  action: string | null
  /** L'entité de la tâche, la consigne appliquée. */
  entiteId: string | null
  ignorer: boolean
  nature: NatureMail
  piecesJointes: number
  lue: (ConsigneLue & { applicable: boolean }) | null
  seulsMailsDeclenches: boolean
}): IssueConsigneAppliquee {
  if (e.action !== 'verifier_mail' && e.action !== 'traiter_demande') return { issue: 'garder' }
  if (e.lue && !e.lue.applicable)
    return { issue: 'laisser', raison: 'Claude lit que la consigne ne s’applique pas à ce mail.' }
  const rien = e.ignorer
    ? 'la consigne ignore ce mail'
    : e.lue && !e.lue.aFaire
      ? 'rien à faire d’après la consigne'
      : null
  if (rien) return e.seulsMailsDeclenches ? { issue: 'fermer', motif: rien } : { issue: 'garder' }
  if (e.action !== 'verifier_mail' || e.entiteId === null) return { issue: 'garder' }
  const { tache } = effetsMail(
    { garder: true, entiteId: e.entiteId, nature: e.nature, aVerifier: [] },
    e.piecesJointes,
    e.lue,
  )
  return tache ? { issue: 'devenir', tache } : { issue: 'garder' }
}

const LIGNE_DOUTE = 'À vérifier : '

/** Description d'une tâche née d'un mail, sa consigne appliquée : le doute retiré, la consigne dite. */
export function descriptionApresConsigne(description: string | null, sujet: string): string {
  const consigne = `Consigne de tri : « ${sujet} ».`
  const lignes = (description ?? '')
    .split('\n')
    .filter((l) => l.trim() && !l.startsWith(LIGNE_DOUTE) && l !== consigne)
  return [...lignes, consigne].join('\n')
}

const couper = (t: string, n = 120) => (t.length > n ? `${t.slice(0, n - 1).trimEnd()}…` : t)

export function titreTacheMail(
  action: ActionTacheMail,
  resume: string | null,
  expediteur: string | null,
): string {
  if (action === 'recuperer_piece')
    return couper(`Récupérer la pièce : ${resume ?? expediteur ?? 'annoncée par mail'}`)
  if (action === 'verifier_mail')
    return couper(resume ? `Vérifier un mail : ${resume}` : 'Vérifier un mail reçu')
  return couper(
    resume ?? (expediteur ? `Demande reçue de ${expediteur}` : 'Demande reçue par mail'),
  )
}

/** Lien vers le mail d'origine, ouvert dans le bon compte Google. */
export function lienMail(adresse: string, gmailId: string): string {
  return `https://mail.google.com/mail/?authuser=${encodeURIComponent(adresse)}#all/${gmailId}`
}

// Pièces venues de la boîte d'une personne non administratrice : elles restent dans son
// périmètre, sinon son courrier sortirait vers les autres entités.

/**
 * Entités où peut aller une pièce ; null : sans restriction, sauf les espaces privés
 * (`privees`) : une pièce commune n'entre jamais dans un espace privé.
 */
export function entiteAdmise(
  entitesPermises: readonly string[] | null,
  entiteId: string | null,
  privees: ReadonlySet<string> | readonly string[] = [],
): boolean {
  if (entitesPermises === null) return cloisonDe(entiteId, privees) === CLOISON_COMMUNE
  return entiteId !== null && entitesPermises.includes(entiteId)
}

/**
 * Motif du refus d'un rattachement manuel (`entiteAdmise` faux), selon la vraie raison : la
 * cible est un espace privé où la pièce n'est pas permise (cloison) ; la pièce vient d'un
 * espace privé et y reste (l'option D6 de la boîte vaut pour ses mails suivants) ; ou elle est
 * bornée aux entités de la personne qui l'a reçue. Ne nomme aucune entité.
 */
export function motifRattachementRefuse(
  entitesPermises: readonly string[] | null,
  cibleId: string,
  privees: ReadonlySet<string> | readonly string[] = [],
): string {
  if (cloisonDe(cibleId, privees) !== CLOISON_COMMUNE)
    return 'Une pièce reçue hors de cet espace privé ne s’y rattache pas : la déposer dans cet espace.'
  if (
    entitesPermises !== null &&
    entitesPermises.length > 0 &&
    entitesPermises.every((id) => cloisonDe(id, privees) !== CLOISON_COMMUNE)
  )
    return 'Pièce d’un espace privé : elle reste dans cet espace. Pour que les prochains mails d’une boîte rattachée à l’espace aillent ailleurs, cocher « Ranger aussi hors de l’espace privé » sur la boîte (Réglages, Connexions).'
  return 'Pièce venue d’une boîte personnelle ou d’un dépôt : elle ne peut être rattachée qu’aux entités de la personne qui l’a reçue.'
}

/** Motif d'une pièce commune qu'un rattachement mènerait dans un espace privé (sans le nommer). */
export const MOTIF_HORS_CLOISON = 'Entité non permise pour cette pièce : à vérifier.'

/**
 * Borne le rattachement automatique d'une pièce restreinte : hors de ses entités, il devient
 * « à vérifier » sur l'entité du mail d'origine. Le motif ne nomme pas l'autre entité.
 */
export function bornerRattachement(
  r: Rattachement,
  entitesPermises: readonly string[] | null,
  entiteSource: string | null,
  privees: ReadonlySet<string> | readonly string[] = [],
): Rattachement {
  if (entiteAdmise(entitesPermises, r.entiteId, privees)) return r
  if (r.issue === 'a_verifier') return { ...r, entiteId: entiteSource }
  return {
    issue: 'a_verifier',
    entiteId: entiteSource,
    motifs: [
      entitesPermises === null
        ? MOTIF_HORS_CLOISON
        : 'Pièce venue d’une boîte personnelle : elle ne peut être rattachée qu’aux entités de sa propriétaire.',
    ],
  }
}

// Reconnexion : quand Google retire l'accès, une tâche pour la propriétaire.

/**
 * Entité de la tâche « reconnecter ». Boîte d'un espace privé où sa propriétaire écrit : cet
 * espace (sans entité, tous les administrateurs liraient son adresse). Sinon, aucune pour un
 * administrateur (il lit ce qui n'a pas d'entité) ; pour une associée, une entité qu'elle lit
 * et où elle écrit, sinon elle ne verrait pas sa propre tâche. Jamais celle d'un accès
 * temporaire (lecture seule).
 */
export function entiteTacheReconnexion(
  perimetre: Perimetre,
  boite: { entiteId: string | null; privee: boolean } | null = null,
): string | null {
  const lecture = perimetre.lecture
  const lue = (id: string) => lecture === 'tout' || lecture.includes(id)
  if (boite?.privee && boite.entiteId) {
    const id = boite.entiteId
    if (perimetre.ecriture.includes(id) && lue(id)) return id
  }
  if (perimetre.administrateur || lecture === 'tout') return null
  return perimetre.ecriture.filter((id) => lecture.includes(id)).sort()[0] ?? null
}

export function titreReconnexion(adresse: string): string {
  return `Reconnecter la boîte ${adresse}`
}

/** Titre sans adresse d'une boîte d'espace privé, distinct par boîte (fin de son id, sans sens). */
export function titreReconnexionMasquee(boiteId: string): string {
  return `Reconnecter une boîte d’un espace privé (réf. ${boiteId.slice(-6)})`
}

/**
 * Titre de la tâche « reconnecter » posée sur `entiteTache` : l'adresse de la boîte, sauf pour
 * une boîte d'un espace privé dont la tâche tombe hors de l'espace (sa propriétaire n'y écrit
 * plus) : lue par d'autres (administrateurs, une entité commune), elle ne porte pas l'adresse.
 */
export function titreTacheReconnexion(
  boite: { id: string; adresse: string; entiteId: string | null; privee: boolean },
  entiteTache: string | null,
): string {
  if (boite.privee && boite.entiteId && entiteTache !== boite.entiteId)
    return titreReconnexionMasquee(boite.id)
  return titreReconnexion(boite.adresse)
}

/** Date limite lue dans le corps : valide, et pas antérieure à la réception du mail. */
export function dateLimiteRetenue(date: string | null, jourReception: string): string | null {
  if (date === null || !z.iso.date().safeParse(date).success) return null
  return date >= jourReception ? date : null
}

// Réponse attendue : une tâche en attente d'un tiers, et un mail ou une pièce qui vient de
// lui (adresses ou domaines connus du tiers). Rattacher et proposer de clore, jamais clore.

export type TacheEnAttente = {
  id: string
  entiteId: string | null
  adresses: readonly string[]
  /** Depuis quand elle attend : la plus ancienne reçoit la réponse. */
  depuis: string
}

/**
 * Tâches qu'un mail de cet expéditeur peut concerner : la plus ancienne en attente reçoit
 * la réponse ; les autres de la même entité sont seulement prévenues (jamais une autre
 * entité, pour ne rien laisser passer d'un périmètre à l'autre).
 */
export function reponsesAttendues(
  attentes: readonly TacheEnAttente[],
  de: string | null,
): { principale: TacheEnAttente; autres: TacheEnAttente[] } | null {
  const touchees = attentes
    .filter((t) => t.adresses.some((m) => expediteurCorrespond(m, de)))
    .sort((a, b) => (a.depuis < b.depuis ? -1 : a.depuis > b.depuis ? 1 : a.id < b.id ? -1 : 1))
  const principale = touchees[0]
  if (!principale) return null
  return {
    principale,
    autres: touchees.slice(1).filter((t) => t.entiteId === principale.entiteId),
  }
}
